import React from "react";

// Color & label maps for zones
export const ZONE_META = {
  long_entry: { label: "Long Entry", color: "#10b981", bg: "rgba(16,185,129,0.14)", border: "#10b981" },
  short_entry: { label: "Short Entry", color: "#f43f5e", bg: "rgba(244,63,94,0.14)", border: "#f43f5e" },
  target: { label: "Target", color: "#f59e0b", bg: "rgba(245,158,11,0.14)", border: "#f59e0b" },
  stop_loss: { label: "Stop Loss", color: "#ef4444", bg: "rgba(239,68,68,0.18)", border: "#ef4444" },
};

export function fmtPrice(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const v = Number(n);
  if (v >= 1000) return v.toLocaleString("de-DE", { maximumFractionDigits: 2 });
  if (v >= 10) return v.toFixed(2);
  return v.toFixed(4);
}

export function zoneRange(z) {
  if (z.price_min === z.price_max) return fmtPrice(z.price_min);
  return `${fmtPrice(z.price_min)} – ${fmtPrice(z.price_max)}`;
}

export function ZoneBadge({ zone }) {
  const m = ZONE_META[zone.type] || ZONE_META.long_entry;
  return (
    <span
      className="inline-flex items-center gap-2 px-2 py-1 text-xs font-mono-num rounded-sm"
      style={{ backgroundColor: m.bg, color: m.color, border: `1px solid ${m.border}` }}
      data-testid={`zone-badge-${zone.type}`}
    >
      <span className="uppercase tracking-wider">{m.label}</span>
      <span style={{ color: m.color }}>{zoneRange(zone)}</span>
    </span>
  );
}

// Vertical price ladder visualizer
export default function ZoneVisualizer({ zones, currentPrice }) {
  if (!zones || zones.length === 0) {
    return (
      <div className="text-muted-foreground text-sm p-4 border border-border">
        Keine Zonen definiert.
      </div>
    );
  }

  const allPrices = zones.flatMap(z => [z.price_min, z.price_max]).filter(p => !isNaN(p));
  if (currentPrice) allPrices.push(currentPrice);
  let min = Math.min(...allPrices);
  let max = Math.max(...allPrices);
  const pad = (max - min) * 0.08 || max * 0.05 || 1;
  min -= pad;
  max += pad;
  const range = max - min;

  const yFor = (p) => 100 - ((p - min) / range) * 100;

  return (
    <div className="relative w-full h-[420px] border border-border bg-background" data-testid="zone-visualizer">
      {/* Grid lines */}
      {[0, 25, 50, 75, 100].map((pct) => {
        const price = max - (pct / 100) * range;
        return (
          <div key={pct} className="absolute left-0 right-0 flex items-center" style={{ top: `${pct}%` }}>
            <div className="flex-1 border-t border-dashed border-border" />
            <span className="font-mono-num text-[10px] text-muted-foreground px-2">{fmtPrice(price)}</span>
          </div>
        );
      })}

      {/* Zones */}
      {zones.map((z) => {
        const m = ZONE_META[z.type] || ZONE_META.long_entry;
        const top = yFor(Math.max(z.price_min, z.price_max));
        const height = Math.max(2, yFor(Math.min(z.price_min, z.price_max)) - top);
        return (
          <div
            key={z.id || `${z.type}-${z.price_min}`}
            className="absolute left-0 right-14 group"
            style={{
              top: `${top}%`,
              height: `${height}%`,
              backgroundColor: m.bg,
              borderTop: `1px solid ${m.border}`,
              borderBottom: `1px solid ${m.border}`,
            }}
            data-testid={`zone-bar-${z.type}`}
          >
            <div className="absolute left-2 top-1 text-[10px] font-display font-bold uppercase tracking-wider"
                 style={{ color: m.color }}>
              {m.label} {z.label ? `· ${z.label}` : ""}
            </div>
            <div className="absolute right-2 bottom-1 text-[10px] font-mono-num text-foreground/70">
              {zoneRange(z)}
            </div>
          </div>
        );
      })}

      {/* Current price marker — red line, visible in both themes */}
      {currentPrice && (
        <div
          className="absolute left-0 right-14 border-t-2 border-rose-500"
          style={{ top: `${yFor(currentPrice)}%` }}
        >
          <div className="absolute -top-3 right-0 bg-rose-500 text-white px-1.5 py-0.5 font-mono-num text-[10px] font-bold">
            {fmtPrice(currentPrice)}
          </div>
        </div>
      )}
    </div>
  );
}
